import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

type Language = 'es' | 'en';

interface Translations {
  [key: string]: string;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'es',
  setLanguage: () => {},
  t: (key: string) => key,
});

const translations: Record<Language, Translations> = {
  es: {
    // Header
    'header.store_question': '\u00bfTienda de inform\u00e1tica o servicios inform\u00e1ticos?',
    'header.become_distributor': 'Hazte Distribuidor',
    'header.special_prices': 'y disfruta de precios especiales.',
    'header.distributor_access': 'Acceso Distribuidores',
    'header.search_placeholder': 'Buscar productos...',
    'header.suggestions': 'Sugerencias',
    'header.select_currency': 'Seleccionar Moneda',
    'header.rates_updated': 'Tasas actualizadas',
    'header.currency': 'Moneda',
    'header.slogan': 'Licencias Originales al Mejor Precio',
    // Nav
    'nav.home': 'Inicio',
    'nav.windows': 'Windows',
    'nav.office': 'Microsft Office 365',
    'nav.antivirus': 'Antivirus',
    'nav.vpn': 'VPN',
    'nav.testimonials': 'Testimonios',
    'nav.become_distributor': 'Hazte Distribuidor',
    'nav.distributor_access': 'Acceso Distribuidores',
    // Footer
    'footer.original_licenses': 'Licencias 100% Originales',
    'footer.instant_delivery': 'Entrega Inmediata',
    'footer.secure_payment': 'Pago Seguro',
    'footer.support_7days': 'Soporte 7 D\u00edas',
    'footer.trusted_store': 'Tu tienda confiable de licencias digitales originales. Entrega inmediata y soporte garantizado.',
    'footer.quick_links': 'Enlaces R\u00e1pidos',
    'footer.support': 'Soporte',
    'footer.contact': 'Contacto',
    'footer.testimonials': 'Testimonios',
    'footer.privacy_policy': 'Pol\u00edtica de Privacidad',
    'footer.terms': 'T\u00e9rminos de Servicio',
    'footer.schedule': 'Horario de Atenci\u00f3n',
    'footer.mon_sun': 'Lunes a Domingo',
    'footer.payment_methods': 'M\u00e9todos de pago aceptados',
    'footer.transfer': 'Transferencia',
    'footer.all_rights': 'Todos los derechos reservados.',
    'footer.distributors': 'Distribuidores',
    'footer.schedule_short': 'Horario',
    'footer.mon_sun_short': 'Lun - Dom',
    'footer.slogan': 'Licencias Originales al Mejor Precio',
    'footer.home': 'Inicio',
    // Home
    'home.windows_licenses': 'Licencias Windows',
    'home.office_licenses': 'Licencias Office 365',
    'home.office_ltsc': 'Licencias Office 365 LTSC',
    'home.antivirus': 'Antivirus',
    'home.vpn': 'VPN - Redes Privadas Virtuales',
    'home.search_results': 'Resultados para',
    'home.clear': 'Limpiar',
    'home.no_products': 'No se encontraron productos',
    'home.no_match': 'No hay productos que coincidan con tu b\u00fasqueda',
    'home.view_all': 'Ver todos los productos',
    'home.store_or_reseller': '\u00bfTIENDA DE INFORM\u00c1TICA O RESELLER?',
    'home.store_desc': '\u00bfEres una tienda de inform\u00e1tica o un revendedor de software? \u00danete a nuestro programa de distribuidores y disfruta de precios especiales y condiciones exclusivas.',
    'home.become_distributor': 'Convi\u00e9rtete en Distribuidor',
    'home.special_prices': 'Precios Especiales',
    'home.exclusive_conditions': 'Condiciones Exclusivas',
    'home.priority_support': 'Soporte Prioritario',
    'home.lowest_prices': 'Precios m\u00e1s bajos',
    'home.lowest_prices_desc': 'Ofrecemos los mejores precios del mercado en licencias de software originales. Ahorra hasta un 70% en tus compras.',
    'home.fast_shipping': 'Env\u00edos r\u00e1pidos',
    'home.fast_shipping_desc': 'Recibe tu licencia al instante por correo electr\u00f3nico. Sin esperas, sin complicaciones. Activaci\u00f3n inmediata.',
    'home.guaranteed': 'Tranquilidad garantizada',
    'home.guaranteed_desc': 'Soporte t\u00e9cnico 24/7 y garant\u00eda de por vida en todas nuestras licencias perpetuas. Tu satisfacci\u00f3n es nuestra prioridad.',
    // Cart
    'cart.your_cart': 'Tu Carrito',
    'cart.products_in_cart': 'productos en tu carrito',
    'cart.product_in_cart': 'producto en tu carrito',
    'cart.empty_cart': 'Tu carrito est\u00e1 vac\u00edo',
    'cart.explore_desc': 'Explora nuestras licencias de software y encuentra las mejores ofertas',
    'cart.explore_products': 'Explorar Productos',
    'cart.discount_code': 'C\u00f3digo de Descuento',
    'cart.enter_code': 'Ingresa tu c\u00f3digo',
    'cart.apply': 'Aplicar',
    'cart.code_applied': '\u00a1C\u00f3digo aplicado!',
    'cart.discount_of': 'de descuento',
    'cart.continue_shopping': 'Continuar comprando',
    'cart.order_summary': 'Resumen de Compra',
    'cart.subtotal': 'Subtotal',
    'cart.shipping': 'Env\u00edo',
    'cart.free': 'Gratis',
    'cart.total': 'Total',
    'cart.proceed_payment': 'Proceder al Pago',
    'cart.secure_payment': 'Pago 100% Seguro',
    'cart.satisfaction_guarantee': 'Garant\u00eda de Satisfacci\u00f3n',
    'cart.guarantee_desc': '30 d\u00edas de garant\u00eda en todas las licencias',
    'cart.what_you_receive': 'Lo que recibir\u00e1s',
    'cart.activation_key': 'Clave de activaci\u00f3n',
    'cart.original_permanent': 'original y permanente',
    'cart.documentation': 'Documentaci\u00f3n',
    'cart.legal_docs': 'conforme a la ley',
    'cart.invoice': 'Factura',
    'cart.electronic_invoice': 'electr\u00f3nica oficial',
    'cart.install_file': 'Archivo de instalaci\u00f3n',
    'cart.download': 'para descargar',
    'cart.finalize_purchase': 'Finalizar Compra',
    'cart.payment_method': 'M\u00e9todo de Pago',
    'cart.your_data': 'Tus Datos',
    'cart.full_name': 'Nombre completo *',
    'cart.email_placeholder': 'Correo electr\u00f3nico *',
    'cart.email_note': 'Aqu\u00ed recibir\u00e1s tu licencia',
    'cart.phone_placeholder': 'Tel\u00e9fono / WhatsApp *',
    'cart.notes_placeholder': 'Notas adicionales (opcional)',
    'cart.info_protected': 'Tu informaci\u00f3n est\u00e1 protegida y segura',
    'cart.order_sent': '\u00a1Pedido Enviado!',
    'cart.request_received': 'Tu solicitud ha sido recibida correctamente.',
    'cart.back_to_store': 'Volver a la Tienda',
    'cart.total_to_pay': 'Total a pagar',
    'cart.pay': 'Pagar',
    'cart.cart': 'Carrito',
    'cart.info': 'Info',
    'cart.payment': 'Pago',
    'cart.digital': 'Digital',
    'cart.immediate_access': 'Acceso inmediato despu\u00e9s de la compra - Recibe tu licencia por email',
    'cart.contact_us': 'o cont\u00e1ctanos',
    'cart.volume_add': 'Agrega',
    'cart.volume_more': 'm\u00e1s y obt\u00e9n',
    'cart.volume_from': 'desde el 3er producto',
    'cart.volume_applied': '\u00a1Descuento por volumen aplicado!',
    'cart.you_save': 'Ahorras',
    'cart.total_saved': 'Total ahorrado',
    'cart.select_payment': 'Selecciona un m\u00e9todo de pago',
    'cart.confirm_order': 'Confirmar Pedido',
    'cart.invalid_code': 'C\u00f3digo de descuento no v\u00e1lido o ya fue utilizado',
    'cart.coupon_error': 'Ocurri\u00f3 un error al intentar aplicar el cup\u00f3n.',
    'cart.sending': 'Enviando...',
    'cart.send_whatsapp': 'Enviar Pedido por WhatsApp',
    'cart.send_whatsapp_short': 'Enviar por WhatsApp',
    'cart.confirm_nequi': 'Confirmar Pedido con Nequi',
    'cart.confirm_efecty': 'Confirmar Pedido con Efecty',
    'cart.confirm_daviplata': 'Confirmar Pedido con Daviplata',
    'cart.pay_mercadopago': 'Pagar con Mercado Pago',
    'cart.pay_paypal': 'Pagar con PayPal',
    'cart.products_count': 'producto(s)',
    'cart.payment_success': '\u00a1Pago Exitoso!',
    'cart.payment_processed': 'Tu pago ha sido procesado correctamente.',
    'cart.order_label': 'Pedido',
    'cart.license_by_email': 'Recibir\u00e1s tu licencia por correo electr\u00f3nico en los pr\u00f3ximos minutos.',
    'cart.payment_pending': 'Pago Pendiente',
    'cart.payment_processing': 'Tu pago est\u00e1 siendo procesado.',
    'cart.notify_email': 'Te notificaremos por correo cuando se confirme el pago.',
    'cart.payment_failed': 'Pago No Completado',
    'cart.payment_failed_desc': 'El pago no pudo ser procesado. Puedes intentar de nuevo o elegir otro m\u00e9todo de pago.',
    'cart.try_again': 'Intentar de Nuevo',
    'cart.preparing_licenses': 'Preparando tus licencias...',
    'cart.preparing_wait': 'Esto puede tomar unos segundos',
    'cart.your_license': 'Tu Licencia',
    'cart.your_licenses': 'Tus Licencias',
    'cart.license_key': 'Clave de Licencia',
    'cart.copied': 'Copiado',
    'cart.copy': 'Copiar',
    'cart.copy_all': 'Copiar Todas',
    'cart.all_copied': '\u00a1Copiadas!',
    'cart.save_whatsapp': 'Guardar en WhatsApp',
    'cart.download_software': 'Descargar Software',
    'cart.license_email_note': 'Tambi\u00e9n recibir\u00e1s tus licencias por correo electr\u00f3nico. Guarda este mensaje.',
    'cart.thanks_purchase': '\u00a1Gracias por tu compra!',
    'cart.promo_desc': 'Aqu\u00ed tienes un 5% de descuento para tu pr\u00f3xima compra:',
    'cart.promo_code_label': 'C\u00f3digo',
    'cart.promo_email_note': 'Recibir\u00e1s este c\u00f3digo por correo electr\u00f3nico en los pr\u00f3ximos minutos.',
    'cart.whatsapp_opened': 'Se abri\u00f3 WhatsApp con tu pedido',
    'cart.send_payment_to': 'Env\u00eda tu pago al n\u00famero',
    'cart.send_receipt': 'Despu\u00e9s env\u00eda el comprobante por WhatsApp para recibir tu licencia al instante.',
    'cart.amount_to_send': 'Monto a enviar por',
    'cart.amount_to_deposit': 'Monto a consignar en Efecty',
    'cart.amount_to_transfer': 'Monto a transferir',
    'cart.equivalent_to': 'Equivale a',
    'cart.nequi_number': 'N\u00famero Nequi',
    'cart.daviplata_number': 'N\u00famero Daviplata',
    'cart.after_payment': 'Despu\u00e9s de pagar, env\u00eda el comprobante por WhatsApp para recibir tu licencia al instante.',
    'cart.nequi_pay': 'Pago con Nequi',
    'cart.nequi_desc': 'Env\u00eda tu pago desde la app Nequi de forma r\u00e1pida y segura.',
    'cart.efecty_pay': 'Pago con Efecty',
    'cart.efecty_desc': 'Dir\u00edgete a un corresponsal Efecty y realiza la consignaci\u00f3n.',
    'cart.efecty_via_nequi': 'Consignaci\u00f3n por medio de Nequi',
    'cart.daviplata_pay': 'Pago con Daviplata',
    'cart.daviplata_desc': 'Env\u00eda tu pago desde la app Daviplata de forma r\u00e1pida.',
    'cart.mercadopago_pay': 'Pago con Mercado Pago',
    'cart.mercadopago_desc': 'Recibir\u00e1s un link de pago donde podr\u00e1s pagar con tarjeta de cr\u00e9dito, d\u00e9bito o saldo disponible.',
    'cart.paypal_pay': 'Pago con PayPal',
    'cart.paypal_desc': 'Recibir\u00e1s un enlace de pago de PayPal para completar la transacci\u00f3n. Acepta tarjetas internacionales y saldo PayPal.',
    'cart.transfer_pay': 'Transferencia Bancolombia',
    'cart.transfer_desc': 'Realiza la transferencia desde tu cuenta personal al banco Bancolombia.',
    'cart.bank': 'Banco',
    'cart.account_type': 'Tipo de cuenta',
    'cart.savings_account': 'Cuenta de Ahorros',
    'cart.account_number': 'N\u00famero de cuenta',
    'cart.scan_qr': 'O escanea el c\u00f3digo QR para pagar',
    'cart.digital_license_payment': 'Pago de Licencia Digital',
    'cart.send_receipt_whatsapp': 'Enviar comprobante por WhatsApp',
    'cart.whatsapp_pay': 'Pago por WhatsApp',
    'cart.whatsapp_desc': 'Se abrir\u00e1 WhatsApp con el resumen de tu pedido. Un asesor te guiar\u00e1 para completar el pago.',
    'cart.discount_5devices': 'Dto. 20% (5 dispositivos)',
    'cart.discount_original': 'Dto. precio original',
    'cart.discount_vpn': 'Dto. VPN',
    'cart.discount_coupon': 'Cup\u00f3n',
    'cart.discount_volume': 'Dto. volumen (5% desde 3er prod.)',
    'cart.off_20': '20% OFF',
    // Product pages
    'product.add_to_cart': 'A\u00f1adir al carrito',
    'product.add': 'A\u00f1adir',
    'product.added': '\u00a1A\u00f1adido!',
    'product.offer': 'Oferta',
    'product.best_seller': 'M\u00e1s Vendido',
    'product.best_price': 'Mejor Precio',
    'product.premium': 'Premium',
    'product.best_option': 'MEJOR OPCI\u00d3N',
    'product.best_short': 'MEJOR',
    'product.monthly': 'Mensuales',
    'product.lifetime': 'Licencia de por vida',
    'product.compare_vpn': 'Comparar VPNs',
    'product.includes': 'Incluye',
    'product.licenses': 'licencias',
    // Windows page
    'windows.title': 'Licencias Windows',
    'windows.subtitle': 'Licencias originales de Windows 10, Windows 11 y Windows Server al mejor precio del mercado.',
    'windows.home_desc': 'Licencia digital original de Windows 11 Home. Perfecta para uso dom\u00e9stico.',
    'windows.10pro_desc': 'Licencia digital original de Windows 10 Professional. Activaci\u00f3n inmediata.',
    'windows.11pro_desc': 'Licencia digital original de Windows 11 Professional. \u00daltima versi\u00f3n.',
    // Office page
    'office.title': 'Licencias Microsoft Office',
    'office.subtitle': 'Office 2021, 2024 y Microsoft 365. Licencias originales con garant\u00eda de por vida, activaci\u00f3n instant\u00e1nea y soporte t\u00e9cnico incluido.',
    'office.original_licenses': 'Licencias 100% Originales',
    'office.lifetime_warranty': 'Garant\u00eda de por vida',
    'office.instant_delivery': 'Entrega inmediata',
    'office.support_247': 'Soporte 24/7',
    'office.apps_title': 'Aplicaciones incluidas en Microsoft Office',
    'office.apps_subtitle': 'Herramientas profesionales de productividad para crear, colaborar y gestionar tu trabajo de forma eficiente.',
    'office.our_licenses': 'Nuestras Licencias de Office',
    'office.choose_license': 'Elige la licencia que mejor se adapte a tus necesidades',
    'office.need_help': '\u00bfNecesitas ayuda para elegir tu licencia?',
    'office.help_desc': 'Nuestro equipo de soporte est\u00e1 disponible para asesorarte y encontrar la mejor opci\u00f3n para ti.',
    'office.contact_whatsapp': 'Contactar por WhatsApp',
    'office.email_instant': 'Env\u00edo por email al instante',
    'office.original_key': 'Clave de activaci\u00f3n original',
    'office.replacement_warranty': 'Garant\u00eda de reemplazo',
    'office.install_guide': 'Gu\u00eda de instalaci\u00f3n incluida',
    // Office apps
    'office.word_desc': 'Crea documentos profesionales con herramientas avanzadas de edici\u00f3n, plantillas y colaboraci\u00f3n en tiempo real.',
    'office.excel_desc': 'Analiza datos con f\u00f3rmulas potentes, tablas din\u00e1micas, gr\u00e1ficos interactivos y funciones de inteligencia artificial.',
    'office.powerpoint_desc': 'Dise\u00f1a presentaciones impactantes con transiciones, animaciones y plantillas de dise\u00f1o profesional.',
    'office.outlook_desc': 'Gestiona tu correo, calendario y contactos en una sola aplicaci\u00f3n con organizaci\u00f3n inteligente.',
    'office.onedrive_desc': 'Almacena y sincroniza tus archivos en la nube con acceso desde cualquier dispositivo y lugar.',
    'office.teams_desc': 'Colabora con videollamadas, chat y espacios de trabajo compartidos para equipos productivos.',
    // Antivirus page
    'antivirus.title': 'Antivirus',
    'antivirus.subtitle': 'Protege tus dispositivos con las mejores soluciones de seguridad: Kaspersky, McAfee, ESET, Norton y m\u00e1s.',
    // VPN page
    'vpn.title': 'VPN - Redes Privadas Virtuales',
    'vpn.subtitle': 'Navega seguro y an\u00f3nimo con las mejores VPN del mercado. Hasta 77% de descuento.',
    // Benefits
    'benefits.instant_delivery': 'Entrega Inmediata',
    'benefits.by_email': 'Por correo electr\u00f3nico',
    'benefits.original': '100% Original',
    'benefits.genuine': 'Licencias genuinas',
    'benefits.support': 'Soporte 24/7',
    'benefits.help': 'Ayuda cuando la necesites',
    'benefits.guarantee': 'Garant\u00eda 30 d\u00edas',
    'benefits.refund': 'Devoluci\u00f3n sin problemas',
    // General
    'general.offer': 'Oferta',
    'general.monthly': 'Mensuales',
    'general.lifetime_license': 'Licencia de por vida',
    'general.buy_now': 'Comprar ahora',
    'general.view_offers': 'Ver ofertas',
    // TrustBadges
    'trust.buy_confidence': 'Compra con confianza',
    'trust.why_choose': '\u00bfPor qu\u00e9 elegirnos?',
    'trust.why_desc': 'Miles de clientes conf\u00edan en nosotros para sus licencias de software. Estas son las razones.',
    'trust.100_original': '100% Originales',
    'trust.100_original_desc': 'Licencias verificadas directamente por los fabricantes oficiales',
    'trust.secure_purchase': 'Compra Segura',
    'trust.secure_purchase_desc': 'Transacciones protegidas con encriptaci\u00f3n SSL de 256 bits',
    'trust.instant_delivery': 'Entrega Inmediata',
    'trust.instant_delivery_desc': 'Recibe tu licencia por email en menos de 5 minutos',
    'trust.lifetime_warranty': 'Garant\u00eda de por Vida',
    'trust.lifetime_warranty_desc': 'Todas las licencias perpetuas incluyen garant\u00eda permanente',
    'trust.spanish_support': 'Soporte en Espa\u00f1ol',
    'trust.spanish_support_desc': 'Atenci\u00f3n personalizada de lunes a domingo, 7 AM a 7 PM',
    'trust.money_back': 'Devoluci\u00f3n Garantizada',
    'trust.money_back_desc': 'Si no funciona tu licencia, te devolvemos el 100% de tu dinero',
    'trust.satisfied_clients': 'Clientes satisfechos',
    'trust.licenses_sold': 'Licencias vendidas',
    'trust.avg_rating': 'Calificaci\u00f3n promedio',
    'trust.successful_deliveries': 'Entregas exitosas',
    'trust.payment_methods': 'M\u00e9todos de pago:',
    'trust.official_partners': 'Partners oficiales:',
    // PromoBanner
    'promo.limited_time': 'Oferta por tiempo limitado',
    'promo.up_to': 'Hasta',
    'promo.off_licenses': 'en Licencias',
    'promo.desc': 'Software 100% original con garant\u00eda de por vida \u2022 Entrega instant\u00e1nea \u2022 Soporte en espa\u00f1ol',
    'promo.ends_in': 'La oferta termina en:',
    'promo.buy_now': 'Comprar Ahora',
    'promo.instant_delivery': 'Entrega inmediata',
    'promo.original_licenses': 'Licencias originales',
    'promo.best_price': 'Mejor precio',
    'promo.support_247': 'Soporte 24/7',
    // ColombianPaymentsBanner
    'colombian.title': 'M\u00e9todos de Pago Colombianos',
    'colombian.heading': 'Paga como prefieras',
    'colombian.subtitle': 'Aceptamos los m\u00e9todos de pago m\u00e1s populares en Colombia',
    'colombian.secure': 'Pagos 100% seguros y verificados',
    'colombian.nequi_desc': 'Paga desde tu celular',
    'colombian.daviplata_desc': 'R\u00e1pido y seguro',
    'colombian.efecty_desc': 'En efectivo o transferencia',
    'colombian.bancolombia_desc': 'Transferencia bancaria',
    // RecommendedProducts
    'recommended.title': 'Productos Recomendados',
    'recommended.add_more': 'Agrega',
    'recommended.more_products': 'más',
    'recommended.get_discount': 'y obtén',
    'recommended.discount_label': '5% de descuento',
    'recommended.complement': 'Complementa tu compra con estas ofertas exclusivas',
    'recommended.volume_near': '¡Estás a',
    'recommended.volume_near2': 'del descuento por volumen!',
    'recommended.volume_desc': 'Agrega cualquier producto y ahorra un 5% adicional desde el 3er artículo',
    'recommended.add_cart': 'Agregar al carrito',
    'recommended.add_short': 'Agregar',
    // NequiBanner
    'nequi.payment_methods': 'Métodos de pagos',
    'nequi.subtitle': 'Paga fácil y seguro con tu método favorito',
    'nequi.buy_now': 'Comprar ahora',
    'nequi.card': 'Tarjeta',
    // Testimonials page
    'testimonials.hero_badge': 'Opiniones reales de clientes verificados',
    'testimonials.hero_title': 'Testimonios de Nuestros Clientes',
    'testimonials.hero_desc_1': 'Más de',
    'testimonials.hero_desc_2': 'clientes',
    'testimonials.hero_desc_3': 'confían en Digital License Store para sus licencias de software originales',
    'testimonials.avg_rating': 'Calificación promedio',
    'testimonials.total_reviews': 'Opiniones totales',
    'testimonials.satisfied': 'Clientes satisfechos',
    'testimonials.recent': 'Reseñas recientes (12 meses)',
    'testimonials.breakdown': 'Desglose de calificaciones',
    'testimonials.stars_5': '5 estrellas',
    'testimonials.stars_4': '4 estrellas',
    'testimonials.stars_3': '3 estrellas',
    'testimonials.stars_2': '2 estrellas',
    'testimonials.stars_1': '1 estrella',
    'testimonials.filter_by': 'Filtrar por',
    'testimonials.all': 'Todos',
    'testimonials.verified': 'Verificado',
    'testimonials.all_verified': 'Todas las reseñas son verificadas',
    'testimonials.all_reviews': 'Todas las opiniones',
    'testimonials.reviews_of': 'Opiniones de',
    'testimonials.stars': 'estrellas',
    'testimonials.results': 'resultados',
    'testimonials.no_reviews': 'No hay opiniones con este filtro',
    'testimonials.secure_purchase': 'Compra Segura',
    'testimonials.secure_desc': 'Pago 100% protegido',
    'testimonials.instant_delivery': 'Entrega Inmediata',
    'testimonials.instant_desc': 'Receive by email instantly',
    'testimonials.lifetime_warranty': 'Garantía de por Vida',
    'testimonials.lifetime_desc': 'On perpetual licenses',
    'testimonials.support_247': 'Soporte 24/7',
    'testimonials.support_desc': 'WhatsApp, teléfono y email',
    // Distributor page
    'distributor.program': 'Programa de Distribuidores',
    'distributor.title': 'Conviértete en Distribuidor',
    'distributor.hero_desc': 'Únete a nuestra red de distribuidores y accede a precios exclusivos, soporte prioritario y las mejores condiciones del mercado para hacer crecer tu negocio.',
    'distributor.request_access': 'Solicitar Acceso',
    'distributor.benefits_title': 'Beneficios Exclusivos para Distribuidores',
    'distributor.benefits_desc': 'Disfruta de ventajas únicas diseñadas para impulsar tu negocio y maximizar tus ganancias.',
    'distributor.exclusive_discounts': 'Descuentos Exclusivos',
    'distributor.exclusive_discounts_desc': 'Accede a precios mayoristas con descuentos de hasta el 40% en todas las licencias.',
    'distributor.vip_program': 'Programa VIP',
    'distributor.vip_program_desc': 'Beneficios adicionales según tu volumen de ventas mensuales.',
    'distributor.priority_support': 'Soporte Prioritario',
    'distributor.priority_support_desc': 'Atención personalizada 24/7 con un ejecutivo de cuenta dedicado.',
    'distributor.instant_delivery': 'Entrega Inmediata',
    'distributor.instant_delivery_desc': 'Receive licenses instantly to deliver to your customers without delays.',
    'distributor.extended_warranty': 'Garantía Extendida',
    'distributor.extended_warranty_desc': 'All licenses include lifetime warranty and technical support.',
    'distributor.control_panel': 'Panel de Control',
    'distributor.control_panel_desc': 'Gestiona tus pedidos, facturas y clientes desde un panel exclusivo.',
    'distributor.how_it_works': '¿Cómo Funciona?',
    'distributor.how_desc': 'Convertirte en distribuidor es muy sencillo. Sigue estos pasos y comienza a vender en menos de 48 horas.',
    'distributor.step1': 'Regístrate',
    'distributor.step1_desc': 'Completa el formulario con los datos de tu empresa.',
    'distributor.step2': 'Verificación',
    'distributor.step2_desc': 'Nuestro equipo verificará tu información en 24-48 horas.',
    'distributor.step3': 'Activación',
    'distributor.step3_desc': 'Recibe tus credenciales y accede a precios exclusivos.',
    'distributor.step4': '¡Empieza a Vender!',
    'distributor.step4_desc': 'Start generating income with our licenses.',
    'distributor.active_distributors': 'Distribuidores Activos',
    'distributor.max_discount': 'Descuento Máximo',
    'distributor.support_available': 'Soporte Disponible',
    'distributor.licenses_sold': 'Licencias Vendidas',
    'distributor.form_title': 'Request Your Distributor Access',
    'distributor.form_desc': 'Complete the form and our team will contact you in less than 24 hours.',
    'distributor.full_name': 'Full Name *',
    'distributor.company_name': 'Company Name *',
    'distributor.email': 'Email Address *',
    'distributor.phone': 'Phone',
    'distributor.message': 'Message (Optional)',
    'distributor.message_placeholder': 'Tell us about your business...',
    'distributor.success_title': 'Application Sent!',
    'distributor.success_desc': 'Thank you for your interest. Our team will review your application and contact you in less than 24 hours.',
    'distributor.send_another': 'Send another application',
    'distributor.error_msg': 'There was an error sending your application. Please try again.',
    'distributor.sending': 'Sending...',
    'distributor.submit': 'Submit Application',
    // VPN product details
    'product.months': 'meses',
    'product.year': 'año',
    'product.years': 'años',
    'product.sold': 'vendidos',
  },
  en: {
    // Header
    'header.store_question': 'Computer store or IT services?',
    'header.become_distributor': 'Become a Distributor',
    'header.special_prices': 'and enjoy special prices.',
    'header.distributor_access': 'Distributor Access',
    'header.search_placeholder': 'Search products...',
    'header.suggestions': 'Suggestions',
    'header.select_currency': 'Select Currency',
    'header.rates_updated': 'Rates updated',
    'header.currency': 'Currency',
    'header.slogan': 'Original Licenses at the Best Price',
    // Nav
    'nav.home': 'Home',
    'nav.windows': 'Windows',
    'nav.office': 'Microsoft Office 365',
    'nav.antivirus': 'Antivirus',
    'nav.vpn': 'VPN',
    'nav.testimonials': 'Testimonials',
    'nav.become_distributor': 'Become a Distributor',
    'nav.distributor_access': 'Distributor Access',
    // Footer
    'footer.original_licenses': '100% Original Licenses',
    'footer.instant_delivery': 'Instant Delivery',
    'footer.secure_payment': 'Secure Payment',
    'footer.support_7days': '7-Day Support',
    'footer.trusted_store': 'Your trusted store for original digital licenses. Instant delivery and guaranteed support.',
    'footer.quick_links': 'Quick Links',
    'footer.support': 'Support',
    'footer.contact': 'Contact',
    'footer.testimonials': 'Testimonials',
    'footer.privacy_policy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.schedule': 'Business Hours',
    'footer.mon_sun': 'Monday to Sunday',
    'footer.payment_methods': 'Accepted payment methods',
    'footer.transfer': 'Transfer',
    'footer.all_rights': 'All rights reserved.',
    'footer.distributors': 'Distributors',
    'footer.schedule_short': 'Hours',
    'footer.mon_sun_short': 'Mon - Sun',
    'footer.slogan': 'Original Licenses at the Best Price',
    'footer.home': 'Home',
    // Home
    'home.windows_licenses': 'Windows Licenses',
    'home.office_licenses': 'Office 365 Licenses',
    'home.office_ltsc': 'Office 365 LTSC Licenses',
    'home.antivirus': 'Antivirus',
    'home.vpn': 'VPN - Virtual Private Networks',
    'home.search_results': 'Results for',
    'home.clear': 'Clear',
    'home.no_products': 'No products found',
    'home.no_match': 'No products match your search',
    'home.view_all': 'View all products',
    'home.store_or_reseller': 'COMPUTER STORE OR RESELLER?',
    'home.store_desc': 'Are you a computer store or software reseller? Join our distributor program and enjoy special prices and exclusive conditions.',
    'home.become_distributor': 'Become a Distributor',
    'home.special_prices': 'Special Prices',
    'home.exclusive_conditions': 'Exclusive Conditions',
    'home.priority_support': 'Priority Support',
    'home.lowest_prices': 'Lowest Prices',
    'home.lowest_prices_desc': 'We offer the best market prices on original software licenses. Save up to 70% on your purchases.',
    'home.fast_shipping': 'Fast Delivery',
    'home.fast_shipping_desc': 'Receive your license instantly by email. No waiting, no hassle. Immediate activation.',
    'home.guaranteed': 'Guaranteed Peace of Mind',
    'home.guaranteed_desc': '24/7 technical support and lifetime warranty on all our perpetual licenses. Your satisfaction is our priority.',
    // Cart
    'cart.your_cart': 'Your Cart',
    'cart.products_in_cart': 'products in your cart',
    'cart.product_in_cart': 'product in your cart',
    'cart.empty_cart': 'Your cart is empty',
    'cart.explore_desc': 'Explore our software licenses and find the best deals',
    'cart.explore_products': 'Explore Products',
    'cart.discount_code': 'Discount Code',
    'cart.enter_code': 'Enter your code',
    'cart.apply': 'Apply',
    'cart.code_applied': 'Code applied!',
    'cart.discount_of': 'discount',
    'cart.continue_shopping': 'Continue shopping',
    'cart.order_summary': 'Order Summary',
    'cart.subtotal': 'Subtotal',
    'cart.shipping': 'Shipping',
    'cart.free': 'Free',
    'cart.total': 'Total',
    'cart.proceed_payment': 'Proceed to Payment',
    'cart.secure_payment': '100% Secure Payment',
    'cart.satisfaction_guarantee': 'Satisfaction Guarantee',
    'cart.guarantee_desc': '30-day warranty on all licenses',
    'cart.what_you_receive': 'What you will receive',
    'cart.activation_key': 'Activation key',
    'cart.original_permanent': 'original and permanent',
    'cart.documentation': 'Documentation',
    'cart.legal_docs': 'legally compliant',
    'cart.invoice': 'Invoice',
    'cart.electronic_invoice': 'official electronic',
    'cart.install_file': 'Installation file',
    'cart.download': 'for download',
    'cart.finalize_purchase': 'Finalize Purchase',
    'cart.payment_method': 'Payment Method',
    'cart.your_data': 'Your Details',
    'cart.full_name': 'Full name *',
    'cart.email_placeholder': 'Email address *',
    'cart.email_note': 'You will receive your license here',
    'cart.phone_placeholder': 'Phone / WhatsApp *',
    'cart.notes_placeholder': 'Additional notes (optional)',
    'cart.info_protected': 'Your information is protected and secure',
    'cart.order_sent': 'Order Sent!',
    'cart.request_received': 'Your request has been received successfully.',
    'cart.back_to_store': 'Back to Store',
    'cart.total_to_pay': 'Total to pay',
    'cart.pay': 'Pay',
    'cart.cart': 'Cart',
    'cart.info': 'Info',
    'cart.payment': 'Payment',
    'cart.digital': 'Digital',
    'cart.immediate_access': 'Immediate access after purchase - Receive your license by email',
    'cart.contact_us': 'or contact us',
    'cart.volume_add': 'Add',
    'cart.volume_more': 'more and get',
    'cart.volume_from': 'from the 3rd product',
    'cart.volume_applied': 'Volume discount applied!',
    'cart.you_save': 'You save',
    'cart.total_saved': 'Total saved',
    'cart.select_payment': 'Select a payment method',
    'cart.confirm_order': 'Confirm Order',
    'cart.invalid_code': 'Invalid or already used discount code',
    'cart.coupon_error': 'An error occurred while applying the coupon.',
    'cart.sending': 'Sending...',
    'cart.send_whatsapp': 'Send Order via WhatsApp',
    'cart.send_whatsapp_short': 'Send via WhatsApp',
    'cart.confirm_nequi': 'Confirm Order with Nequi',
    'cart.confirm_efecty': 'Confirm Order with Efecty',
    'cart.confirm_daviplata': 'Confirm Order with Daviplata',
    'cart.pay_mercadopago': 'Pay with Mercado Pago',
    'cart.pay_paypal': 'Pay with PayPal',
    'cart.products_count': 'product(s)',
    'cart.payment_success': 'Payment Successful!',
    'cart.payment_processed': 'Your payment has been processed successfully.',
    'cart.order_label': 'Order',
    'cart.license_by_email': 'You will receive your license by email in the next few minutes.',
    'cart.payment_pending': 'Payment Pending',
    'cart.payment_processing': 'Your payment is being processed.',
    'cart.notify_email': 'We will notify you by email when the payment is confirmed.',
    'cart.payment_failed': 'Payment Not Completed',
    'cart.payment_failed_desc': 'The payment could not be processed. You can try again or choose another payment method.',
    'cart.try_again': 'Try Again',
    'cart.preparing_licenses': 'Preparing your licenses...',
    'cart.preparing_wait': 'This may take a few seconds',
    'cart.your_license': 'Your License',
    'cart.your_licenses': 'Your Licenses',
    'cart.license_key': 'License Key',
    'cart.copied': 'Copied',
    'cart.copy': 'Copy',
    'cart.copy_all': 'Copy All',
    'cart.all_copied': 'Copied!',
    'cart.save_whatsapp': 'Save to WhatsApp',
    'cart.download_software': 'Download Software',
    'cart.license_email_note': 'You will also receive your licenses by email. Save this message.',
    'cart.thanks_purchase': 'Thank you for your purchase!',
    'cart.promo_desc': 'Here is a 5% discount for your next purchase:',
    'cart.promo_code_label': 'Code',
    'cart.promo_email_note': 'You will receive this code by email in the next few minutes.',
    'cart.whatsapp_opened': 'WhatsApp opened with your order',
    'cart.send_payment_to': 'Send your payment to number',
    'cart.send_receipt': 'Then send the receipt via WhatsApp to receive your license instantly.',
    'cart.amount_to_send': 'Amount to send via',
    'cart.amount_to_deposit': 'Amount to deposit at Efecty',
    'cart.amount_to_transfer': 'Amount to transfer',
    'cart.equivalent_to': 'Equivalent to',
    'cart.nequi_number': 'Nequi Number',
    'cart.daviplata_number': 'Daviplata Number',
    'cart.after_payment': 'After paying, send the receipt via WhatsApp to receive your license instantly.',
    'cart.nequi_pay': 'Pay with Nequi',
    'cart.nequi_desc': 'Send your payment from the Nequi app quickly and securely.',
    'cart.efecty_pay': 'Pay with Efecty',
    'cart.efecty_desc': 'Go to an Efecty agent and make the deposit.',
    'cart.efecty_via_nequi': 'Deposit via Nequi',
    'cart.daviplata_pay': 'Pay with Daviplata',
    'cart.daviplata_desc': 'Send your payment from the Daviplata app quickly.',
    'cart.mercadopago_pay': 'Pay with Mercado Pago',
    'cart.mercadopago_desc': 'You will receive a payment link where you can pay with credit card, debit card, or available balance.',
    'cart.paypal_pay': 'Pay with PayPal',
    'cart.paypal_desc': 'You will receive a PayPal payment link to complete the transaction. Accepts international cards and PayPal balance.',
    'cart.transfer_pay': 'Bancolombia Transfer',
    'cart.transfer_desc': 'Make the transfer from your personal account to Bancolombia bank.',
    'cart.bank': 'Bank',
    'cart.account_type': 'Account type',
    'cart.savings_account': 'Savings Account',
    'cart.account_number': 'Account number',
    'cart.scan_qr': 'Or scan the QR code to pay',
    'cart.digital_license_payment': 'Digital License Payment',
    'cart.send_receipt_whatsapp': 'Send receipt via WhatsApp',
    'cart.whatsapp_pay': 'Pay via WhatsApp',
    'cart.whatsapp_desc': 'WhatsApp will open with your order summary. An advisor will guide you to complete the payment.',
    'cart.discount_5devices': '20% OFF (5 devices)',
    'cart.discount_original': 'Original price discount',
    'cart.discount_vpn': 'VPN Discount',
    'cart.discount_coupon': 'Coupon',
    'cart.discount_volume': 'Volume discount (5% from 3rd prod.)',
    'cart.off_20': '20% OFF',
    // Product pages
    'product.add_to_cart': 'Add to cart',
    'product.add': 'Add',
    'product.added': 'Added!',
    'product.offer': 'Sale',
    'product.best_seller': 'Best Seller',
    'product.best_price': 'Best Price',
    'product.premium': 'Premium',
    'product.best_option': 'BEST OPTION',
    'product.best_short': 'BEST',
    'product.monthly': 'Monthly',
    'product.lifetime': 'Lifetime license',
    'product.compare_vpn': 'Compare VPNs',
    'product.includes': 'Includes',
    'product.licenses': 'licenses',
    // Windows page
    'windows.title': 'Windows Licenses',
    'windows.subtitle': 'Original Windows 10, Windows 11 and Windows Server licenses at the best market price.',
    'windows.home_desc': 'Original Windows 11 Home digital license. Perfect for home use.',
    'windows.10pro_desc': 'Original Windows 10 Professional digital license. Instant activation.',
    'windows.11pro_desc': 'Original Windows 11 Professional digital license. Latest version.',
    // Office page
    'office.title': 'Microsoft Office Licenses',
    'office.subtitle': 'Office 2021, 2024 and Microsoft 365. Original licenses with lifetime warranty, instant activation and technical support included.',
    'office.original_licenses': '100% Original Licenses',
    'office.lifetime_warranty': 'Lifetime warranty',
    'office.instant_delivery': 'Instant delivery',
    'office.support_247': '24/7 Support',
    'office.apps_title': 'Applications included in Microsoft Office',
    'office.apps_subtitle': 'Professional productivity tools to create, collaborate and manage your work efficiently.',
    'office.our_licenses': 'Our Office Licenses',
    'office.choose_license': 'Choose the license that best suits your needs',
    'office.need_help': 'Need help choosing your license?',
    'office.help_desc': 'Our support team is available to advise you and find the best option for you.',
    'office.contact_whatsapp': 'Contact via WhatsApp',
    'office.email_instant': 'Instant email delivery',
    'office.original_key': 'Original activation key',
    'office.replacement_warranty': 'Replacement warranty',
    'office.install_guide': 'Installation guide included',
    // Office apps
    'office.word_desc': 'Create professional documents with advanced editing tools, templates and real-time collaboration.',
    'office.excel_desc': 'Analyze data with powerful formulas, pivot tables, interactive charts and AI features.',
    'office.powerpoint_desc': 'Design stunning presentations with transitions, animations and professional design templates.',
    'office.outlook_desc': 'Manage your email, calendar and contacts in one app with smart organization.',
    'office.onedrive_desc': 'Store and sync your files in the cloud with access from any device and location.',
    'office.teams_desc': 'Collaborate with video calls, chat and shared workspaces for productive teams.',
    // Antivirus page
    'antivirus.title': 'Antivirus',
    'antivirus.subtitle': 'Protect your devices with the best security solutions: Kaspersky, McAfee, ESET, Norton and more.',
    // VPN page
    'vpn.title': 'VPN - Virtual Private Networks',
    'vpn.subtitle': 'Browse safely and anonymously with the best VPNs on the market. Up to 77% off.',
    // Benefits
    'benefits.instant_delivery': 'Instant Delivery',
    'benefits.by_email': 'By email',
    'benefits.original': '100% Original',
    'benefits.genuine': 'Genuine licenses',
    'benefits.support': '24/7 Support',
    'benefits.help': 'Help when you need it',
    'benefits.guarantee': '30-Day Guarantee',
    'benefits.refund': 'Hassle-free returns',
    // General
    'general.offer': 'Sale',
    'general.monthly': 'Monthly',
    'general.lifetime_license': 'Lifetime license',
    'general.buy_now': 'Buy now',
    'general.view_offers': 'View offers',
    // TrustBadges
    'trust.buy_confidence': 'Buy with confidence',
    'trust.why_choose': 'Why choose us?',
    'trust.why_desc': 'Thousands of customers trust us for their software licenses. Here are the reasons.',
    'trust.100_original': '100% Original',
    'trust.100_original_desc': 'Licenses verified directly by official manufacturers',
    'trust.secure_purchase': 'Secure Purchase',
    'trust.secure_purchase_desc': 'Transactions protected with 256-bit SSL encryption',
    'trust.instant_delivery': 'Instant Delivery',
    'trust.instant_delivery_desc': 'Receive your license by email in less than 5 minutes',
    'trust.lifetime_warranty': 'Lifetime Warranty',
    'trust.lifetime_warranty_desc': 'All perpetual licenses include permanent warranty',
    'trust.spanish_support': 'Spanish Support',
    'trust.spanish_support_desc': 'Personalized attention Monday to Sunday, 7 AM to 7 PM',
    'trust.money_back': 'Money-Back Guarantee',
    'trust.money_back_desc': 'If your license doesn\\\'t work, we refund 100% of your money',
    'trust.satisfied_clients': 'Satisfied clients',
    'trust.licenses_sold': 'Licenses sold',
    'trust.avg_rating': 'Average rating',
    'trust.successful_deliveries': 'Successful deliveries',
    'trust.payment_methods': 'Payment methods:',
    'trust.official_partners': 'Official partners:',
    // PromoBanner
    'promo.limited_time': 'Limited time offer',
    'promo.up_to': 'Up to',
    'promo.off_licenses': 'OFF on Licenses',
    'promo.desc': '100% original software with lifetime warranty \u2022 Instant delivery \u2022 Support in Spanish',
    'promo.ends_in': 'Offer ends in:',
    'promo.buy_now': 'Buy Now',
    'promo.instant_delivery': 'Instant delivery',
    'promo.original_licenses': 'Original licenses',
    'promo.best_price': 'Best price',
    'promo.support_247': '24/7 Support',
    // ColombianPaymentsBanner
    'colombian.title': 'Colombian Payment Methods',
    'colombian.heading': 'Pay your way',
    'colombian.subtitle': 'We accept the most popular payment methods in Colombia',
    'colombian.secure': '100% secure and verified payments',
    'colombian.nequi_desc': 'Pay from your phone',
    'colombian.daviplata_desc': 'Fast and secure',
    'colombian.efecty_desc': 'Cash or transfer',
    'colombian.bancolombia_desc': 'Bank transfer',
    // RecommendedProducts
    'recommended.title': 'Recommended Products',
    'recommended.add_more': 'Add',
    'recommended.more_products': 'more',
    'recommended.get_discount': 'and get',
    'recommended.discount_label': '5% discount',
    'recommended.complement': 'Complement your purchase with these exclusive deals',
    'recommended.volume_near': 'You\\\'re',
    'recommended.volume_near2': 'away from the volume discount!',
    'recommended.volume_desc': 'Add any product and save an additional 5% from the 3rd item',
    'recommended.add_cart': 'Add to cart',
    'recommended.add_short': 'Add',
    // NequiBanner
    'nequi.payment_methods': 'Payment Methods',
    'nequi.subtitle': 'Pay easily and securely with your favorite method',
    'nequi.buy_now': 'Buy now',
    'nequi.card': 'Card',
    // Testimonials page
    'testimonials.hero_badge': 'Real reviews from verified customers',
    'testimonials.hero_title': 'Customer Testimonials',
    'testimonials.hero_desc_1': 'More than',
    'testimonials.hero_desc_2': 'customers',
    'testimonials.hero_desc_3': 'trust Digital License Store for their original software licenses',
    'testimonials.avg_rating': 'Average rating',
    'testimonials.total_reviews': 'Total reviews',
    'testimonials.satisfied': 'Satisfied customers',
    'testimonials.recent': 'Recent reviews (12 months)',
    'testimonials.breakdown': 'Rating breakdown',
    'testimonials.stars_5': '5 stars',
    'testimonials.stars_4': '4 stars',
    'testimonials.stars_3': '3 stars',
    'testimonials.stars_2': '2 stars',
    'testimonials.stars_1': '1 star',
    'testimonials.filter_by': 'Filter by',
    'testimonials.all': 'All',
    'testimonials.verified': 'Verified',
    'testimonials.all_verified': 'All reviews are verified',
    'testimonials.all_reviews': 'All reviews',
    'testimonials.reviews_of': 'Reviews of',
    'testimonials.stars': 'stars',
    'testimonials.results': 'results',
    'testimonials.no_reviews': 'No reviews with this filter',
    'testimonials.secure_purchase': 'Secure Purchase',
    'testimonials.secure_desc': '100% protected payment',
    'testimonials.instant_delivery': 'Instant Delivery',
    'testimonials.instant_desc': 'Receive by email instantly',
    'testimonials.lifetime_warranty': 'Lifetime Warranty',
    'testimonials.lifetime_desc': 'On perpetual licenses',
    'testimonials.support_247': '24/7 Support',
    'testimonials.support_desc': 'WhatsApp, phone and email',
    // Distributor page
    'distributor.program': 'Distributor Program',
    'distributor.title': 'Become a Distributor',
    'distributor.hero_desc': 'Join our distributor network and access exclusive prices, priority support and the best market conditions to grow your business.',
    'distributor.request_access': 'Request Access',
    'distributor.benefits_title': 'Exclusive Benefits for Distributors',
    'distributor.benefits_desc': 'Enjoy unique advantages designed to boost your business and maximize your profits.',
    'distributor.exclusive_discounts': 'Exclusive Discounts',
    'distributor.exclusive_discounts_desc': 'Access wholesale prices with discounts of up to 40% on all licenses.',
    'distributor.vip_program': 'VIP Program',
    'distributor.vip_program_desc': 'Additional benefits based on your monthly sales volume.',
    'distributor.priority_support': 'Priority Support',
    'distributor.priority_support_desc': 'Personalized 24/7 attention with a dedicated account executive.',
    'distributor.instant_delivery': 'Instant Delivery',
    'distributor.instant_delivery_desc': 'Receive licenses instantly to deliver to your customers without delays.',
    'distributor.extended_warranty': 'Extended Warranty',
    'distributor.extended_warranty_desc': 'All licenses include lifetime warranty and technical support.',
    'distributor.control_panel': 'Control Panel',
    'distributor.control_panel_desc': 'Gestiona tus pedidos, facturas y clientes desde un panel exclusivo.',
    'distributor.how_it_works': 'How Does It Work?',
    'distributor.how_desc': 'Becoming a distributor is very simple. Follow these steps and start selling in less than 48 hours.',
    'distributor.step1': 'Register',
    'distributor.step1_desc': 'Complete the form with your company details.',
    'distributor.step2': 'Verification',
    'distributor.step2_desc': 'Our team will verify your information in 24-48 hours.',
    'distributor.step3': 'Activation',
    'distributor.step3_desc': 'Receive your credentials and access exclusive prices.',
    'distributor.step4': 'Start Selling!',
    'distributor.step4_desc': 'Start generating income with our licenses.',
    'distributor.active_distributors': 'Active Distributors',
    'distributor.max_discount': 'Maximum Discount',
    'distributor.support_available': 'Support Available',
    'distributor.licenses_sold': 'Licenses Sold',
    'distributor.form_title': 'Request Your Distributor Access',
    'distributor.form_desc': 'Complete the form and our team will contact you in less than 24 hours.',
    'distributor.full_name': 'Full Name *',
    'distributor.company_name': 'Company Name *',
    'distributor.email': 'Email Address *',
    'distributor.phone': 'Phone',
    'distributor.message': 'Message (Optional)',
    'distributor.message_placeholder': 'Tell us about your business...',
    'distributor.success_title': 'Application Sent!',
    'distributor.success_desc': 'Thank you for your interest. Our team will review your application and contact you in less than 24 hours.',
    'distributor.send_another': 'Send another application',
    'distributor.error_msg': 'There was an error sending your application. Please try again.',
    'distributor.sending': 'Sending...',
    'distributor.submit': 'Submit Application',
    // VPN product details
    'product.months': 'months',
    'product.year': 'year',
    'product.years': 'years',
    'product.sold': 'sold',
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('preferred_language');
      if (saved === 'es' || saved === 'en') return saved;
    } catch {
      // ignore
    }
    // Detectar idioma del navegador
    const browserLang = navigator.language?.toLowerCase() || '';
    if (browserLang.startsWith('es')) return 'es';
    return 'es'; // Default español
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('preferred_language', lang);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key: string, fallback?: string): string => {
    return translations[language][key] || fallback || translations.es[key] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
